// File: src/components/fans/FanLeaderboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import app from '../../services/firebaseConfig';
import UserProfilePicture from '../profile/UserProfilePicture';
import { useNavigate } from 'react-router-dom';

import { BADGE_TIERS, calculateBadgeTier } from '../../config/constants';

const db = getFirestore(app);

const FanLeaderboard = ({ 
    artistId, 
    isDashboardView = false, 
    allowSelfTipping = false,   // Default to false for public views
    hideOwner = true,           // 🌟 Default to true so owners are hidden on public scoreboards
    showOwnerBadge = true,      
    ownerBadgeText = "Owner",   
    onMetricsCalculated 
}) => {
    const [rawCache, setRawCache] = useState({ fansAllTime: [], fansThisMonth: [], fans: [] });
    const [timeFrame, setTimeFrame] = useState('this_month'); 
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [artistProfile, setArtistProfile] = useState(null);
    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const handleArtistHeaderClick = () => {
        if (!artistId) return;
        navigate(`/profile/${artistId}`);
    };

    const handleFanRowClick = (e, fanUserId) => {
        e.stopPropagation();
        if (!fanUserId) return;
        navigate(`/profile/${fanUserId}`);
    };

    // Fetch Artist Profile Metadata
    useEffect(() => {
        if (!artistId) return;
        const fetchArtistMetadata = async () => {
            try {
                const artistSnap = await getDoc(doc(db, 'artist_accounts', artistId));
                if (artistSnap.exists()) {
                    setArtistProfile(artistSnap.data());
                }
            } catch (err) {
                console.error("[Leaderboard Header Info Drop]", err);
            }
        };
        fetchArtistMetadata();
    }, [artistId]);

    // Fetch Cached Leaderboard Snapshot
    useEffect(() => {
        const fetchSingleReadSnapshot = async () => {
            if (!artistId) return;
            setLoading(true);
            try {
                const leaderboardDocRef = doc(db, 'fan_leaderboards', artistId);
                const snap = await getDoc(leaderboardDocRef);

                if (snap.exists()) {
                    setRawCache(snap.data());
                }
            } catch (err) {
                console.error("Error reading fan_leaderboards snapshot:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchSingleReadSnapshot();
    }, [artistId]);

    // 🌟 Memoized list filtered by selected timeFrame and owner constraints
    const displayedFans = useMemo(() => {
        const isMonthView = timeFrame === 'this_month';
        const rawList = isMonthView 
            ? (rawCache.fansThisMonth?.length > 0 ? rawCache.fansThisMonth : rawCache.fansAllTime || rawCache.fans || [])
            : (rawCache.fansAllTime || rawCache.fans || []);

        // 🌟 1. Filter out Owner and Self-Tippers for public views
        const filtered = rawList.filter(fan => {
            const fanUid = fan.userId || fan.id;
            const isFanOwner = fan.isOwner === true || fanUid === artistId;

            if (hideOwner && isFanOwner) return false;
            if (!allowSelfTipping && fanUid === artistId) return false;
            return true;
        });

        const formatted = filtered.map(fan => {
            // 🌟 2. Extract accurate points depending on the tab
            let points = 0;
            if (isMonthView) {
                points = Number(fan.monthlyPoints) || Number(fan.pointsThisMonth) || Number(fan.rollingScore) || Number(fan.totalPointsTipped) || 0;
            } else {
                points = Number(fan.totalPointsTipped) || Number(fan.overallFanScore) || Number(fan.allTimePoints) || 0;
            }

            const badgeTier = fan.badgeTier || calculateBadgeTier(points);

            return {
                id: fan.id || fan.userId,
                userId: fan.userId || fan.id,
                fanName: fan.fanName || fan.displayName || "Anonymous Fan",
                fanAvatar: fan.fanAvatar || fan.photoURL || "",
                totalPointsTipped: points,
                badgeTier: badgeTier,
                isPremium: Boolean(fan.isPremium),
                isOwner: Boolean(fan.isOwner || (fan.userId === artistId))
            };
        });

        // Trigger callback metrics calculation
        if (onMetricsCalculated) {
            let fansCount = 0;
            let supaFansCount = 0;
            let ultraFansCount = 0;

            formatted.forEach(fan => {
                const pts = fan.totalPointsTipped;
                if (pts >= BADGE_TIERS.FAN.minPoints) fansCount++;
                if (pts >= BADGE_TIERS.SUPAFAN.minPoints) supaFansCount++;
                if (pts >= BADGE_TIERS.ULTRAFAN.minPoints) ultraFansCount++;
            });

            onMetricsCalculated({
                uniqueTippers: fansCount,
                supaFans: supaFansCount,
                ultraFans: ultraFansCount
            });
        }

        return formatted;
    }, [rawCache, timeFrame, hideOwner, allowSelfTipping, artistId, onMetricsCalculated]);

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 rounded-3xl p-6 md:p-8 shadow-sm space-y-6 max-h-[85vh] flex flex-col">
            
            {/* HEADER MODULE */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5 flex-shrink-0">
                <div className="flex items-center gap-3.5">
                    <div 
                        onClick={handleArtistHeaderClick}
                        className="cursor-pointer hover:scale-105 transition-transform flex-shrink-0"
                    >
                        <UserProfilePicture 
                            photoURL={artistProfile?.photoURL || artistProfile?.avatarURL || artistProfile?.artistAvatar} 
                            sizeClass="w-12 h-12"
                            isPremium={false}
                        />
                    </div>

                    <div>
                        <h3 className="text-lg font-black tracking-tight text-slate-800 dark:text-white leading-tight">
                            {isDashboardView ? "SupaFan Scoreboard" : (artistProfile?.artistName || "Loading Artist...")}
                        </h3>
                        <button 
                            onClick={() => setIsInfoOpen(true)}
                            className="flex items-center gap-1 text-xs text-slate-400 font-bold hover:text-[#ff00cc] transition-colors mt-0.5"
                        >
                            <span>{isDashboardView ? "Metrics & Rankings" : "SupaFan Scoreboard"}</span>
                            <span className="w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-600 flex items-center justify-center text-[9px] font-black">i</span>
                        </button>
                    </div>
                </div>

                {/* TIME-FRAME SELECTOR PILL & TOP FANS PILL */}
                <div className="flex items-center gap-3">
                    <div className="bg-slate-100/80 dark:bg-slate-800/80 p-0.5 rounded-xl flex gap-1 h-8 items-center border border-slate-200/80 dark:border-slate-700/80">
                        <button 
                            onClick={() => setTimeFrame('this_month')}
                            className={`px-3 h-7 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                                timeFrame === 'this_month' 
                                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-xs' 
                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                            }`}
                        >
                            This Month
                        </button>
                        <button 
                            onClick={() => setTimeFrame('all_time')}
                            className={`px-3 h-7 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                                timeFrame === 'all_time' 
                                    ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-xs' 
                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                            }`}
                        >
                            All-Time
                        </button>
                    </div>

                    {/* 🌟 Top Fans Pill reflects only visible fans */}
                    <span className="text-xs font-black uppercase text-[#ff00cc] bg-[#ff00cc]/10 px-3 py-1.5 rounded-full border border-[#ff00cc]/30 hidden sm:inline-block">
                        {displayedFans.length} {displayedFans.length === 1 ? 'Top Fan' : 'Top Fans'}
                    </span>
                </div>
            </header>

            {/* SCROLLABLE LIST CONTAINER */}
            <div className="flex-grow overflow-y-auto custom-scrollbar pr-1 space-y-3 min-h-0">
                {loading ? (
                    <div className="text-center py-10 text-xs font-bold uppercase tracking-widest text-slate-400 animate-pulse">
                        Calculating Fan Points...
                    </div>
                ) : displayedFans.length === 0 ? (
                    <div className="text-center py-12 text-sm text-slate-400 font-bold tracking-tight">
                        No leaderboard entries yet. Boost a track to take the #1 fan spot!
                    </div>
                ) : (
                    displayedFans.map((fan, index) => {
                        const isTopThree = index < 3;
                        const rankColors = [
                            'bg-amber-100 text-amber-800 border-amber-200',
                            'bg-slate-100 text-slate-700 border-slate-200',
                            'bg-orange-100 text-orange-800 border-orange-200'
                        ];

                        const totalPoints = fan.totalPointsTipped;
                        const currentBadgeTier = fan.badgeTier;

                        // 🌟 Dedicated Border Color per Badge Tier:
                        // UltraFan = Pink | SupaFan = Blue | Fan = Green
                        const avatarBorderClass = currentBadgeTier === 'ULTRAFAN'
                            ? 'ring-2 ring-[#ff00cc]'
                            : currentBadgeTier === 'SUPAFAN'
                                ? 'ring-2 ring-[#3b82f6]'
                                : 'ring-2 ring-[#22c55e]'; // Green for standard Fan

                        return (
                            <div 
                                key={fan.userId || fan.id || index}
                                onClick={(e) => handleFanRowClick(e, fan.userId || fan.id)}
                                className="flex items-center justify-between p-4 border border-slate-50 dark:border-slate-800/40 rounded-2xl bg-white dark:bg-slate-900 hover:bg-slate-50/60 dark:hover:bg-slate-800/20 transition-all group shadow-2sm cursor-pointer"
                            >
                                <div className="flex items-center gap-4">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-xs border ${
                                        isTopThree ? rankColors[index] : 'bg-gray-50 text-gray-400 dark:bg-slate-800 dark:border-slate-700'
                                    }`}>
                                        {index + 1}
                                    </span>

                                    <UserProfilePicture 
                                        photoURL={fan.fanAvatar} 
                                        sizeClass="w-10 h-10"
                                        badgeTier={currentBadgeTier} 
                                        shape="circle"
                                    />

                                    <div>
                                        <div className="flex items-center gap-2">
                                            {/* 🌟 Name is always solid dark / black text */}
                                            <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight">
                                                {fan.fanName}
                                            </h4>
                                            
                                            {showOwnerBadge && fan.isOwner && (
                                                <span className="text-[9px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/30">
                                                    {ownerBadgeText}
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Tier Label */}
                                        <p className="text-[10px] font-black uppercase tracking-wider mt-0.5">
                                            {currentBadgeTier === 'ULTRAFAN' ? (
                                                <span className="text-[#ff00cc]">
                                                    {fan.isPremium ? 'Premium UltraFan' : 'UltraFan'}
                                                </span>
                                            ) : currentBadgeTier === 'SUPAFAN' ? (
                                                <span className="text-[#3b82f6]">
                                                    {fan.isPremium ? 'Premium SupaFan' : 'SupaFan'}
                                                </span>
                                            ) : (
                                                <span className="text-[#22c55e]">
                                                    {fan.isPremium ? 'Premium Fan' : 'Fan'}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <span className="font-black text-sm text-slate-900 dark:text-white">
                                        {totalPoints.toLocaleString()} pts
                                    </span>
                                    {/* 🌟 Renamed uniformly to "FAN SCORE" across both tabs */}
                                    <span className="block text-[9px] font-black text-slate-400 uppercase tracking-wider mt-0.5">
                                        Fan Score
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* INFO DIALOG OVERLAY */}
            {isInfoOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-800 max-w-sm w-full rounded-2xl p-6 shadow-2xl space-y-4 animate-in zoom-in-95 duration-200">
                        <h4 className="text-base font-black tracking-tight text-[#ff00cc] uppercase">How Fan Scores Work</h4>
                        
                        <div className="text-xs text-slate-500 dark:text-slate-400 space-y-3 font-semibold leading-relaxed">
                            <p>
                                <strong className="text-slate-800 dark:text-white">Fan Points:</strong> Earned by boosting tracks to back your favorite artists. Your total contributed points rank you on artist scoreboards and level up both you and your artists community status.
                            </p>
                            
                            <div className="border-t border-slate-100 dark:border-slate-800 pt-2 space-y-1 text-[11px]">
                                <p>
                                    <strong className="text-[#22c55e]">Fan Tier:</strong> {BADGE_TIERS.FAN.minPoints.toLocaleString()}+ Points
                                </p>
                                <p>
                                    <strong className="text-[#3b82f6]">SupaFan Tier:</strong> {BADGE_TIERS.SUPAFAN.minPoints.toLocaleString()}+ Points
                                </p>
                                <p>
                                    <strong className="text-[#ff00cc]">UltraFan Tier:</strong> {BADGE_TIERS.ULTRAFAN.minPoints.toLocaleString()}+ Points
                                </p>
                            </div>
                        </div>

                        <button 
                            onClick={() => setIsInfoOpen(false)}
                            className="w-full py-2.5 bg-slate-900 dark:bg-slate-800 hover:bg-black text-white rounded-xl text-xs font-black uppercase transition-colors"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default FanLeaderboard;